import { Link, useLocation, useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import { useAuth, apiClient } from "../auth";
import { MapPin, Sparkles, Eye, Utensils, LayoutDashboard, LogIn, LogOut, UserPlus, Menu, X, Database, UserCircle2, Trophy, Siren, BellRing, HandHeart, MessageSquare, ShieldCheck, PawPrint, Shield, EyeOff, Home, Mail, Plus, Users } from "lucide-react";
import PatiAsistan from "./PatiAsistan";
import { LiveCameraShutterProvider } from "../context/LiveCameraShutterContext";
import UploadProgressBar from "./UploadProgressBar";
import CatMilestoneModal from "./FirstCatMilestone";
import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import NotificationBell from "./NotificationBell";
import { useBadgeCelebration } from "../hooks/useBadgeCelebration";
import AnimalToggleModal from "./AnimalToggleModal";
import { getAnimalMode, setAnimalMode } from "../animalMode";
import { getCountry } from "../country";

// Drawer (hamburger) destinations — order = discovery priority.
const drawerLinks = [
  { to: "/map", key: "nav.map", icon: MapPin, color: "text-blue-600" },
  { to: "/feeding", key: "nav.feeding", icon: Utensils, color: "text-orange-600" },
  { to: "/observation-house", key: "nav.observationHouse", icon: Home, color: "text-amber-700" },
  { to: "/scan-cat", key: "nav.identify", icon: Sparkles, color: "text-orange-600" },
  { to: "/cats", key: "nav.database", icon: Database, color: "text-stone-600" },
  { to: "/leaderboard", key: "nav.leaderboard", icon: Trophy, color: "text-blue-600" },
  { to: "/volunteers", key: "nav.volunteers", icon: HandHeart, color: "text-rose-600" },
  { to: "/badges", key: "nav.badges", icon: PawPrint, color: "text-amber-600" },
  { to: "/safety", key: "nav.safety", icon: Shield, color: "text-emerald-700" },
];

// Two DISTINCT footer columns. The old footer showed the first six links under
// "Keşfet" and then ALL eight again under "Topluluk", so every link appeared
// twice — a wall of repeated text. Split them: what you use on the left, the
// community on the right, nothing in both.
const _byPath = Object.fromEntries(drawerLinks.map((l) => [l.to, l]));
const footerDiscoverLinks = ["/map", "/scan-cat", "/cats", "/observation-house"]
  .map((p) => _byPath[p])
  .filter(Boolean);
const footerCommunityLinks = ["/volunteers", "/leaderboard", "/badges", "/safety"]
  .map((p) => _byPath[p])
  .filter(Boolean);

/**
 * The phone buzzes and the bell shakes when Acil is tapped.
 *
 * Not decoration. This is a native <a> doing a full-page navigation, and in an
 * Android WebView that can take a visible beat. Someone standing over a bleeding
 * cat who presses the button and feels nothing happen will press it again, and
 * again, and start to believe the app is broken. The buzz is the receipt: it
 * lands in the same instant as the finger, before any network does anything.
 */
function triggerEmergencyTapHaptic() {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(35);
    }
  } catch {
    /* Vibration API unsupported or blocked — silent noop (e.g. iOS Safari). */
  }
}

function playEmergencyBellShake(bellEl) {
  if (!bellEl) return;
  // Remove, force a reflow, re-add. Without the reflow the browser coalesces the
  // class removal and addition into no change at all, and the animation only
  // ever plays once — on the first tap of the session.
  bellEl.classList.remove("emergency-mobile-link__bell--shake");
  void bellEl.offsetWidth;
  bellEl.classList.add("emergency-mobile-link__bell--shake");
}

export default function Layout({ children }) {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  // GÜN1-TOGGLE: tür seçici (kedi / köpek-yakında / hepsi). Seçim
  // localStorage'da; v1 kedi odaklı olduğu için veri tarafında fark yaratmaz,
  // köpek verisi geldiğinde aynı anahtar API süzgecine bağlanacak.
  const [turSeciciAcik, setTurSeciciAcik] = useState(false);
  const [turModu, setTurModu] = useState(getAnimalMode);
  const turSec = (m) => setTurModu(setAnimalMode(m));

  // Unread direct-message count → drives the orange badge on the Messages tab.
  // Polls lightly and refetches on every navigation, so opening the thread (which
  // marks it read) clears the badge on the way back out.
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  useEffect(() => {
    if (!user) {
      setUnreadMsgs(0);
      return;
    }
    let alive = true;
    const fetchUnread = () => {
      apiClient()
        .get("/notifications/unread-count")
        .then((r) => {
          if (alive) setUnreadMsgs(r.data?.messages || 0);
        })
        .catch(() => {});
    };
    fetchUnread();
    // 25 sn → 90 sn (25 Tem 2026). Bu yoklama kullanıcı hiçbir şey yapmasa bile
    // sürüyor ve maliyeti eşzamanlı kullanıcı sayısıyla doğrusal artıyor:
    //   25 sn'de bir →   300 kullanıcı =  12 istek/sn · 10.000 kullanıcı = 400/sn
    //   90 sn'de bir →   300 kullanıcı = 3.3 istek/sn · 10.000 kullanıcı = 111/sn
    // Yani sadece bildirim sayacı için harcanan yük dörde bölünüyor.
    //
    // Kullanıcı deneyimi bozulmuyor: rota her değiştiğinde de yeniden çekiliyor
    // (aşağıdaki bağımlılık dizisinde `loc.pathname` var), yani uygulamada
    // gezinen biri sayacı zaten anında güncel görüyor. 90 sn yalnızca ekranda
    // hareketsiz duran kullanıcı için geçerli.
    const id = setInterval(fetchUnread, 90000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user, loc.pathname]);

  // Apply premium theme to <html data-theme="...">
  useEffect(() => {
    const isPremiumActive = !!(user?.is_premium && user?.premium_until);
    const theme = isPremiumActive ? (user?.theme || "warm_stone") : "warm_stone";
    document.documentElement.setAttribute("data-theme", theme);
  }, [user]);

  const emergencyBellRef = useRef(null);

  // Detect newly-earned badges on every route change and fire celebration.
  useBadgeCelebration(loc.pathname);

  // Global route progress bar — flashes a brief gradient line across the
  // top of the screen on EVERY internal navigation (Link, useNavigate,
  // bottom dock, etc.) for a consistent SPA "loading" feel.
  // The Acil Bildir <a href> click handler also toggles this class
  // pre-navigation so the bar appears even during a full-page reload.
  useEffect(() => {
    document.body.classList.add("route-loading");
    const t = setTimeout(() => document.body.classList.remove("route-loading"), 650);
    return () => clearTimeout(t);
  }, [loc.pathname]);

  return (
    <LiveCameraShutterProvider>
    <div className="min-h-screen flex flex-col bg-stone-50 text-stone-800">
      {/* GÜN1-TOGGLE: logodan açılan tür seçici. */}
      <AnimalToggleModal
        isOpen={turSeciciAcik}
        onClose={() => setTurSeciciAcik(false)}
        onSelect={turSec}
        country={(getCountry() || "").toUpperCase()}
        currentMode={turModu}
      />
      {/* Top loading bar — flashes when user taps Acil Bildir so the
          full-page <a> navigation feels instant in Android WebView. */}
      <div className="route-progress" aria-hidden />

      {/* ─── Acil Bildir — STANDALONE, fixed top-right, plain <a> tag ───
          Rendered OUTSIDE the sticky header so no glass-nav backdrop-filter,
          transform, or overflow:hidden parent can swallow the tap in
          Android WebView. Uses native anchor (full-page navigation) — the
          most resilient tap target on any WebView version. */}
      <a
        href="/emergency-report"
        className="emergency-mobile-link"
        data-testid="nav-emergencies-link"
        aria-label={t("nav.emergencies")}
        onClick={() => {
          // Don't preventDefault — let the native navigation happen. Everything
          // here is instant feedback for the beat before the WebView catches up.
          triggerEmergencyTapHaptic();
          playEmergencyBellShake(emergencyBellRef.current);
          document.body.classList.add("route-loading");
        }}
      >
        <span ref={emergencyBellRef} className="emergency-mobile-link__bell" aria-hidden>
          <BellRing size={21} strokeWidth={2.25} />
        </span>
        <span className="emergency-mobile-link__label">ACİL</span>
      </a>

      <header className={`sticky top-0 z-40 glass-nav ${loc.pathname.startsWith("/messages") ? "hidden" : ""}`} data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          {/* GÜN1-TOGGLE: logoya basınca tür seçici açılır (kedi / köpek-yakında
              / hepsi); uygulama adı eskisi gibi anasayfaya götürür. */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setTurSeciciAcik(true)}
              className="w-9 h-9 rounded-xl overflow-hidden shadow-sm hover:rotate-[-6deg] transition-transform"
              aria-label={t("toggle.selectMode") || "Tür seç"}
              data-testid="logo-animal-toggle"
            >
              <img src="/app-icon.png" alt={t("appName")} className="w-full h-full object-cover" />
            </button>
            <Link to="/" className="font-display font-bold text-base tracking-tight" data-testid="logo-link">
              {t("appName")}
            </Link>
          </div>

          {/* Global language toggle — on every page EXCEPT the landing page ("/"),
              which already has its own toggle in the hero bar (showing it in both
              places is a duplicate). Sits LEFT of the fixed ACİL pill (~16px from
              the screen edge, ~100px wide), so the right margin keeps the two from
              overlapping. Drives i18n app-wide via setLang — one tap switches the
              whole UI. */}
          {loc.pathname !== "/" && (
          <div
            className="flex items-center gap-0.5 bg-stone-100/90 backdrop-blur rounded-full p-0.5 mr-28 shadow-sm border border-stone-200"
            data-testid="header-lang-toggle"
          >
            <button
              type="button"
              onClick={() => setLang("tr")}
              aria-pressed={lang === "tr"}
              data-testid="header-lang-tr"
              className={`px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${lang === "tr" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`}
            >
              TR
            </button>
            <button
              type="button"
              onClick={() => setLang("en")}
              aria-pressed={lang === "en"}
              data-testid="header-lang-en"
              className={`px-2.5 py-1 rounded-full text-xs font-bold transition-colors ${lang === "en" ? "bg-white text-stone-900 shadow-sm" : "text-stone-500"}`}
            >
              EN
            </button>
          </div>
          )}

          {/* Acil Bildir is rendered OUTSIDE the header as a fixed
              top-right anchor (see below) so no header layer / glass-nav
              backdrop-filter can swallow the WebView tap.
              Messages access now lives inside the Mahallem page header
              so the bottom nav stays at the Golden 5. */}
        </div>
      </header>

      <main className="flex-1" data-testid="main-content">{children}</main>

      {/* Footer, tam ekran çalışan sayfalarda gizli.
          /messages: sohbet kutusu alt kenara kadar iniyor.
          /map: harita sayfanın kendisi; altında marka şeridi görünmesi haritayı
          yukarı sıkıştırıyordu ve alttaki + tuşuyla çakışıyordu. */}
      <footer
        className={`border-t border-stone-200 bg-white ${
          loc.pathname.startsWith("/messages") || loc.pathname.startsWith("/map") ? "hidden" : ""
        }`}
        data-testid="app-footer"
      >
        <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl overflow-hidden shadow-sm">
                <img src="/app-icon.png" alt={t("appName")} className="w-full h-full object-cover" />
              </span>
              <div className="font-display font-bold text-base tracking-tight">{t("appName")}</div>
            </div>
            <p className="text-xs text-stone-500 mt-4 leading-relaxed max-w-xs">
              {t("footer.brandDescription")}
            </p>
            {/* Harita künyesi. Haritaların üstündeki "i" tuşu kaldırıldı (alttaki
                + tuşuyla çakışıyor ve reklam gibi duruyordu), ama atıf zorunlu:
                altlık CARTO / OpenStreetMap, uydu görüntüsü Esri. Bu satırı
                silmek servislerin kullanım koşullarını ihlal eder. */}
            <p className="text-[10px] text-stone-400 mt-3 leading-relaxed" data-testid="footer-map-credit">
              Harita: © CARTO, © OpenStreetMap katkıcıları · Uydu: © Esri
            </p>
          </div>

          {/* Discover (primary nav) */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-3">
              {t("footer.discover") || "Discover"}
            </div>
            <ul className="space-y-2 text-sm">
              {footerDiscoverLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-stone-600 hover:text-orange-700 transition-colors inline-flex items-center gap-1.5">
                    <l.icon size={13} className={l.color} />
                    {t(l.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Community (secondary nav) */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-3">
              {t("footer.community") || "Community"}
            </div>
            <ul className="space-y-2 text-sm">
              {footerCommunityLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-stone-600 hover:text-orange-700 transition-colors inline-flex items-center gap-1.5">
                    <l.icon size={13} className="text-stone-400" />
                    {t(l.key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 font-semibold mb-3">
              {t("footer.connect") || "Connect"}
            </div>
            {/* Only real links. The Twitter/Instagram/GitHub icons pointed at
                twitter.com / instagram.com / github.com — the sites' front doors,
                not accounts we own. A link that goes nowhere real is worse than
                no link, and App Review notices. Left standing: the one address a
                person can actually reach us at. */}
            <div className="flex items-center gap-2">
              <a
                href="mailto:hello@straypaw.ai"
                aria-label="E-posta"
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-stone-200 text-sm text-stone-600 hover:text-orange-600 hover:border-orange-300 transition-colors"
                data-testid="footer-email-link"
              >
                <Mail size={16} /> hello@straypaw.ai
              </a>
            </div>
            <p className="text-[11px] text-stone-400 mt-4">
              © 2026 StrayPaw ·{" "}
              <Link
                to="/gizlilik-politikasi"
                data-testid="footer-privacy-link"
                className="hover:text-orange-700 hover:underline"
              >
                Gizlilik Politikası
              </Link>
            </p>
          </div>
        </div>
      </footer>

      {/* BOTTOM NAV — visible on all screen sizes. On mobile it spans full
          width pinned to the edge; on desktop it floats as a centered dock
          (max-width capped, glass effect, gentle gap from screen bottom). */}
      <nav
        className="fixed bottom-0 left-0 right-0 md:bottom-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:max-w-md md:rounded-2xl md:shadow-2xl z-40 mobile-bottom-nav"
        data-testid="mobile-bottom-nav"
      >
        <div className="grid grid-cols-5 h-16 items-end">
          {[
            { to: "/", label: t("nav.home") || "Anasayfa", icon: Home, key: "home" },
            { to: "/map", label: t("nav.map") || "Harita", icon: MapPin, key: "map" },
            // The big centre button is the front door to the whole app: point a
            // camera, and the AI searches the database before it lets you file a
            // new cat. That flow lives at /scan-cat. It USED to point at
            // /identify, a "Yakında / bakımda" placeholder from the period CLIP
            // was disabled — so the loudest button in the app opened a dead end
            // and dumped people onto the manual form. The flow works now; send
            // them to it.
            { to: "/scan-cat", label: t("nav.identify") || "Tanı", icon: Sparkles, key: "identify", center: true },
            { to: user ? "/messages" : "/login", label: t("nav.messages") || "Mesajlar", icon: MessageSquare, key: "messages" },
            { to: user ? "/profile" : "/login", label: t("nav.profile"), icon: UserCircle2, key: "profile" },
          ].map((item) => {
            const Icon = item.icon;
            const active =
              item.to === "/"
                ? loc.pathname === "/"
                : loc.pathname.startsWith(item.to);
            if (item.center) {
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  data-testid={`mobile-nav-${item.key}`}
                  className="relative flex items-center justify-center"
                >
                  {/* Soft pulsing glow ring behind the primary action. */}
                  <span
                    aria-hidden
                    className="absolute -top-3 w-16 h-16 rounded-full bg-orange-500 opacity-60 blur-lg cta-identify-pulse"
                  />
                  <span
                    className={`relative w-16 h-16 rounded-full grid place-items-center shadow-2xl shadow-orange-900/45 ring-4 ring-white -translate-y-4 transition-transform active:scale-95 ${
                      active ? "bg-orange-700" : "bg-orange-600 hover:bg-orange-700"
                    }`}
                  >
                    <Icon size={28} strokeWidth={2.2} className="text-white cta-identify-sparkle" />
                  </span>
                  {/* Mini label under the floating disc */}
                  <span className="absolute -bottom-0.5 text-[10px] font-bold text-orange-700 truncate max-w-[64px]">
                    {item.label}
                  </span>
                </Link>
              );
            }
            const hasUnread = item.key === "messages" && unreadMsgs > 0;
            return (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`mobile-nav-${item.key}`}
                className={`flex flex-col items-center justify-center gap-0.5 h-full transition-colors ${
                  active || hasUnread ? "text-orange-600" : "text-stone-500 hover:text-stone-900"
                }`}
              >
                <span className="relative">
                  <Icon size={hasUnread ? 22 : 20} className={hasUnread ? "transition-all" : ""} />
                  {hasUnread && (
                    <span
                      data-testid="mobile-nav-messages-badge"
                      className="absolute -top-2 -right-2.5 min-w-[17px] h-[17px] px-1 rounded-full bg-orange-600 text-white text-[10px] font-bold leading-[17px] text-center ring-2 ring-white animate-pulse"
                    >
                      {unreadMsgs > 9 ? "9+" : unreadMsgs}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-medium truncate max-w-[60px]">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Pati Asistan — floating fab to the right of the bottom dock.
          Stays accessible without intruding on the 5-item primary nav. */}
      <PatiAsistan />

      {/* Live upload progress — floats above the dock when a photo POST
          is in flight. Auto-hides on completion / failure. */}
      <UploadProgressBar />

      {/* Cat-count milestone modal — 1st, 5th, 10th, 25th tiers */}
      <CatMilestoneModal />
    </div>
    </LiveCameraShutterProvider>
  );
}
